/**
 * ChatView — text chat UI for the embedded widget. Mirrors the surface of
 * apps/wunderguide-web/src/components/drawers/ChatContent.tsx but trimmed:
 * no socket listener (no LiveKit yet), no persona banner (the widget header
 * already shows partner branding), and tighter spacing for 380×560.
 */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  getMergedHistory,
  streamChatMessage,
  type ChatHistoryItem,
  type ChatMessageItem,
  type SendChatMessageResponse,
} from '../api/chatApi';
import type { QuickRepliesContentItem, QuickReplyOption } from '@wg/types';
import { useInstance } from '../context/InstanceContext';
import { pickLocalizedString } from '../lang/pickLocalizedString';
import { useChatSession } from '../context/ChatSessionContext';
import { useVoice } from '../context/VoiceContext';
import { useView } from '../context/ViewContext';
import { useCheckout } from '../context/CheckoutContext';
import { useGatedAddItem } from '../hooks/useGatedAddItem';
import { useRemoveItineraryByRef } from '../hooks/useRemoveItineraryByRef';
import { useParentBridge } from '../context/ParentBridgeContext';
import { emit as emitTelemetry } from '../utils/telemetry';
import MicOrb from '../components/MicOrb';
import Spinner from '../common/Spinner';
import {
  DEFAULT_THEME,
  renderItem,
  type RenderCtx,
  type ResponseMeta,
} from '../chat/registry';
import type { ActionDeps } from '../chat/actions';
import { initiateBookingForSession } from '../api/checkoutApi';
import { httpClient } from '../client';

type Sender = 'user' | 'assistant' | 'system';

// Fallback suggestions when the instance config doesn't supply any —
// these keep the empty state from feeling empty for a fresh install.
// Partner-supplied suggestions in `i18n.suggestions` always win.

interface ThreadMessage {
  id: string;
  sender: Sender;
  content: ChatMessageItem[];
  at: string;
  pending?: boolean;
  failed?: boolean;
  /**
   * Per-message metadata. We only set this on the optimistic user reply
   * to an interactive prompt (chip tap, form submit, confirm). Used to
   * highlight the answered chip / inert form state in history. Server-
   * persisted metadata isn't currently round-tripped through /history,
   * so this is best-effort until the user navigates away and back.
   */
  meta?: ResponseMeta;
}

const POLL_MS = 2000;

const ChatView: React.FC = () => {
  const { t } = useTranslation();
  const { locale, config, effectiveLocale } = useInstance();
  const { chatSessionId: sessionId } = useChatSession();
  // Suggestions resolution: localized strings table > legacy single-lang
  // i18n.suggestions > built-in defaults. Localized lookup falls through
  // full tag → base → 'en'; if no row covers the visitor's locale, we
  // honor whatever the legacy field has so unmigrated instances keep
  // their pre-translation behavior.
  const localizedSuggestions = pickLocalizedString<string[]>(
    config?.strings,
    'suggestions',
    effectiveLocale,
  );
  const partnerSuggestions =
    (localizedSuggestions && localizedSuggestions.length > 0
      ? localizedSuggestions
      : config?.i18n?.suggestions) ?? [];
  // Welcome copy: localized strings table (set per-language in portal)
  // wins; legacy persona.welcome_messages.default is the back-compat
  // fallback for unmigrated instances; built-in i18n key is the final
  // fallback so a brand-new instance has something readable.
  const localizedWelcome = pickLocalizedString<string>(
    config?.strings,
    'welcome_message',
    effectiveLocale,
  );
  const legacyWelcome = (
    config?.persona as { welcome_messages?: Record<string, string> } | undefined
  )?.welcome_messages?.default;
  const welcomeCopy =
    localizedWelcome || legacyWelcome || t('chat.welcome_copy');
  // Pre-populated cards/items shown in the empty state — admin-authored on
  // partner_embedded_instances.persona.seed_content_items. Same wire shape
  // as agent-emitted content_items so we can reuse the same renderers
  // (CardRenderer, MapRenderer, etc.). Cleared once the visitor sends
  // their first message — they live in the empty state, not in chat
  // history. Scalable: per-instance, declarative, no agent round-trip.
  const seedContentItems = useMemo(() => {
    const raw = (
      config?.persona as { seed_content_items?: unknown } | undefined
    )?.seed_content_items;
    if (!Array.isArray(raw)) return [] as ChatMessageItem[];
    return raw.filter(
      (it): it is ChatMessageItem =>
        !!it &&
        typeof it === 'object' &&
        typeof (it as { type?: unknown }).type === 'string',
    );
  }, [config?.persona]);
  const defaultSuggestions = useMemo(
    () => [
      t('chat.default_suggestion_help'),
      t('chat.default_suggestion_top_experiences'),
      t('chat.default_suggestion_plan_day'),
    ],
    [t],
  );
  const suggestions =
    partnerSuggestions.length > 0 ? partnerSuggestions : defaultSuggestions;
  const usingDefaults = partnerSuggestions.length === 0;
  const voiceEnabled =
    (config?.feature_flags as Record<string, unknown> | undefined)
      ?.voice_enabled === true;
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const voice = useVoice();
  const voiceActive = voice.isListening || voice.isConnecting;
  const { view: currentView, setView } = useView();
  const { setCart, setPrefillContact } = useCheckout();
  // Auth-gated: agent add_to_itinerary for a guest queues + opens AuthView.
  const addItineraryItem = useGatedAddItem();
  const removeItineraryItem = useRemoveItineraryByRef();
  const parentBridge = useParentBridge();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const messagesRef = useRef<ThreadMessage[]>([]);
  messagesRef.current = messages;
  // Voice-session content arrives over the LiveKit room data channel (see
  // useEmbedAudioChat). Each batch is appended to the thread exactly once;
  // this ref tracks which batch ids have already been drained so a
  // re-render or a history poll doesn't double-append them.
  const consumedVoiceBatchIds = useRef<Set<string>>(new Set());

  // Merge server messages over optimistic ones (drop optimistic when server
  // confirms with same role + text within ±5min — generous window to absorb
  // device/server clock skew, which was triggering false survivors).
  const mergeServerMessages = useCallback((server: ThreadMessage[]) => {
    setMessages(prev => {
      const optimistic = prev.filter(
        m => m.id.startsWith('optimistic-') || m.pending,
      );
      const survivors = optimistic.filter(om => {
        const omText = String(om.content[0]?.text ?? '').trim();
        if (!omText) return true;
        const omTime = parseUtcTime(om.at);
        return !server.some(sm => {
          if (sm.sender !== om.sender) return false;
          const smText = String(sm.content[0]?.text ?? '').trim();
          if (smText !== omText) return false;
          const smTime = parseUtcTime(sm.at);
          // NaN/Invalid Date guard — if either timestamp is unparseable,
          // fall back to text-only match (rather than letting NaN<=N fail).
          if (Number.isNaN(omTime) || Number.isNaN(smTime)) return true;
          return Math.abs(smTime - omTime) <= 300_000;
        });
      });
      const sortedServer = [...server].sort(
        (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
      );
      return [...sortedServer, ...survivors];
    });
  }, []);

  // Drain new voice-delivered content batches into the chat thread. Voice
  // content items are NOT persisted to chat history server-side, so — unlike
  // SSE text-chat content — they can't be recovered by the history poll.
  // They live as client-side optimistic messages for the session; the
  // `optimistic-` id prefix keeps mergeServerMessages from dropping them.
  //
  // Two item kinds in a batch are handled distinctly:
  //   • `transcript` items (worker.py transcribe_and_log → wg.content) carry
  //     `speaker` ∈ {user, assistant} and `text`. Each becomes its OWN
  //     ThreadMessage so a back-and-forth voice exchange appears as a
  //     normal turn-by-turn chat in the scroll view (and autoscroll lands
  //     on the latest line). Sender maps from item.speaker.
  //   • Everything else (cards, html, app_actions…) bundles into a single
  //     assistant ThreadMessage as before — agent-emitted rich content
  //     accompanies the assistant turn.
  useEffect(() => {
    const fresh = voice.voiceContentBatches.filter(
      b => !consumedVoiceBatchIds.current.has(b.id),
    );
    if (fresh.length === 0) return;
    fresh.forEach(b => consumedVoiceBatchIds.current.add(b.id));

    const additions: ThreadMessage[] = [];
    fresh.forEach(b => {
      const rich: ChatMessageItem[] = [];
      (b.items as ChatMessageItem[]).forEach((item, idx) => {
        if (item.type === 'transcript') {
          const speaker = item.speaker as string | undefined;
          const text = String(item.text ?? '').trim();
          if (!text) return;
          additions.push({
            id: `optimistic-voice-${b.id}-t${idx}`,
            sender: speaker === 'user' ? 'user' : 'assistant',
            content: [{ type: 'text', text }] as ChatMessageItem[],
            at: b.at,
          });
        } else {
          rich.push(item);
        }
      });
      if (rich.length > 0) {
        additions.push({
          id: `optimistic-voice-${b.id}`,
          sender: 'assistant',
          content: rich,
          at: b.at,
        });
      }
    });
    if (additions.length > 0) {
      setMessages(prev => [...prev, ...additions]);
    }
  }, [voice.voiceContentBatches]);

  // Load merged history; refresh every POLL_MS.
  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = async (initial: boolean) => {
      if (initial) setLoading(true);
      try {
        const data = await getMergedHistory(sessionId, 50, 0);
        if (cancelled) return;
        const fromHistory: ThreadMessage[] = data.messages.map(
          (m: ChatHistoryItem) => ({
            id: m.id,
            sender: m.role,
            content: m.content,
            at: m.created_at,
          }),
        );
        if (initial) setMessages(fromHistory);
        else mergeServerMessages(fromHistory);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        const e = err as { response?: { status?: number } };
        if (initial && e?.response?.status !== 404) {
          setError('Failed to load history.');
        }
      } finally {
        if (initial && !cancelled) setLoading(false);
      }
    };

    load(true);
    timer = setInterval(() => load(false), POLL_MS);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [sessionId, mergeServerMessages]);

  const submitText = useCallback(
    async (rawText: string, meta?: ResponseMeta) => {
      const text = rawText.trim();
      if (!text || !sessionId || sending) return;

      const optimisticBase = Date.now();
      const userMsgId = `optimistic-user-${optimisticBase}`;
      const streamingMsgId = `optimistic-streaming-${optimisticBase}`;
      const userMsg: ThreadMessage = {
        id: userMsgId,
        sender: 'user',
        content: [{ type: 'text', text }],
        at: new Date().toISOString(),
        pending: true,
        meta,
      };
      // Streaming bubble starts as the typing indicator; the first delta
      // replaces it with text in-place. Done event upgrades to final form.
      const streamingMsg: ThreadMessage = {
        id: streamingMsgId,
        sender: 'assistant',
        content: [{ type: 'loading', text: 'Thinking…' }],
        at: new Date().toISOString(),
        pending: true,
      };
      setMessages(prev => [...prev, userMsg, streamingMsg]);
      setDraft('');
      setSending(true);

      emitTelemetry('chat_message_sent', {
        sessionId,
        props: { role: 'user', text, length: text.length },
      });

      const history = messagesRef.current
        .slice(-10)
        .filter(m => m.content.some(c => c.type === 'text'))
        .map(m => {
          const textItem = m.content.find(c => c.type === 'text');
          let content = String(
            (textItem && 'text' in textItem ? textItem.text : '') ?? '',
          );
          // Production mirror of the booking-fix in tests/e2e/conftest.py
          // (Phase 10b partial). Append a compact `[surfaced this turn:
          // <id>=<title>, …]` tag to each assistant message so the brain's
          // selector on the NEXT turn can resolve "book it" / "book the
          // whole thing" against the catalog_product_ids of items the
          // visitor just saw, instead of falling back to search_activities.
          // See memory entry [[project_hawaiifun_broader_commit_gap]].
          if (m.sender === 'assistant') {
            const surfaced = extractSurfacedIds(m.content);
            if (surfaced.length) {
              const tag = surfaced
                .map(([id, title]) => `${id}=${JSON.stringify(title)}`)
                .join(', ');
              content = `${content}\n[surfaced this turn: ${tag}]`;
            }
          }
          return {
            role: (m.sender === 'assistant' ? 'assistant' : 'user') as
              | 'user'
              | 'assistant',
            content,
          };
        });

      // Boxed in an object so TypeScript's narrowing in the closures below
      // doesn't collapse the outer-scope refs to `never` after the await.
      // streamedContent is a sparse array indexed by server-side position
      // so onDone can fall back to streamed cards when finalPayload.content
      // comes back empty (synthesizer didn't surface them, but stream did).
      const state: {
        accumulated: string;
        finalPayload: SendChatMessageResponse | null;
        streamErr: Error | null;
        streamedContent: ChatMessageItem[];
      } = {
        accumulated: '',
        finalPayload: null,
        streamErr: null,
        streamedContent: [],
      };

      try {
        await streamChatMessage(
          sessionId,
          text,
          {
            onDelta(delta) {
              state.accumulated += delta;
              const current = state.accumulated;
              // flushSync forces React to commit + paint synchronously
              // before this callback returns. Without it, React 18 batches
              // every onDelta's setMessages into one late render — the
              // user only sees the final accumulated text, not streaming.
              //
              // CRITICAL: mutate the text item in place. We used to
              // replace `content` with `[{type:'text', text:current}]`
              // every delta, which wiped any card spliced in by
              // onContent — that's why cards flashed and vanished on
              // every subsequent text chunk.
              flushSync(() => {
                setMessages(prev =>
                  prev.map(m => {
                    if (m.id !== streamingMsgId) return m;
                    const next = [...m.content];
                    // Find or create the text item. The bubble starts as
                    // [loading]; first delta replaces that slot with text
                    // and keeps it as the running text accumulator.
                    const textIdx = next.findIndex(c => c.type === 'text');
                    const loadingIdx = next.findIndex(
                      c => c.type === 'loading',
                    );
                    if (textIdx >= 0) {
                      next[textIdx] = { type: 'text', text: current };
                    } else if (loadingIdx >= 0) {
                      next[loadingIdx] = { type: 'text', text: current };
                    } else {
                      next.unshift({ type: 'text', text: current });
                    }
                    return { ...m, content: next };
                  }),
                );
              });
            },
            onContent(item, position) {
              // Mid-stream content item — splice into the streaming bubble
              // at `position`. The streaming bubble already holds an
              // accumulating `text` item at index 0; positions are
              // server-side indices into the assistant's final content
              // array, so position+1 keeps the running text first.
              //
              // Track the item in a side-buffer so onDone can merge it
              // back even if the server's persisted content[] comes back
              // empty — the previous code dropped the streamed card on
              // every done event with empty server content.
              state.streamedContent[position] = item as ChatMessageItem;
              flushSync(() => {
                setMessages(prev =>
                  prev.map(m => {
                    if (m.id !== streamingMsgId) return m;
                    const next = [...m.content];
                    // Find existing slot for this position (one past the
                    // text item). Replace if present (idempotent re-emit),
                    // otherwise insert in order.
                    const targetIdx = Math.min(position + 1, next.length);
                    next.splice(targetIdx, 0, item as never);
                    return { ...m, content: next };
                  }),
                );
              });
            },
            onDone(payload) {
              state.finalPayload = payload;
            },
            onError(err) {
              state.streamErr = err;
            },
          },
          { history, language: locale, metadata: meta },
        );
      } catch (err) {
        state.streamErr = err instanceof Error ? err : new Error(String(err));
      }

      if (state.streamErr && !state.finalPayload && !state.accumulated) {
        setMessages(prev =>
          prev
            .filter(m => m.id !== streamingMsgId)
            .map(m => (m.id === userMsgId ? { ...m, failed: true } : m)),
        );
        setError('Failed to send.');
        setSending(false);
        return;
      }

      // Build the finalized assistant message. Server's text is authoritative
      // (it's the persisted transcript); for content we prefer server's
      // persisted cards but fall back to whatever streamed if the server's
      // content[] is empty — that combo handles both:
      //   • streamed card + server persistence (typical happy path) — server
      //     content wins, identical to what was streamed
      //   • streamed card + empty server content (synthesizer didn't
      //     surface them, but a tool emitted them) — keep the streamed card
      //     instead of letting it visibly vanish on done
      const responseText = state.finalPayload?.response ?? state.accumulated;
      const responseContent: ChatMessageItem[] = [];
      if (responseText)
        responseContent.push({ type: 'text', text: responseText });
      const serverContent = state.finalPayload?.content ?? [];
      if (serverContent.length > 0) {
        for (const item of serverContent) responseContent.push(item);
      } else {
        // Sparse array → compact, preserving stream order.
        for (const item of state.streamedContent) {
          if (item) responseContent.push(item);
        }
      }

      // Finalize the streaming bubble IN PLACE rather than removing it and
      // appending a separate asst_optimistic. The race we're avoiding: server
      // logs the assistant row before flushing SSE done, so a 2s poll can
      // land between server-log and client-done. That poll's merge drops
      // streamingMsg (its accumulated text matches server's transcript) and
      // brings in asst_real. If we then concat asst_optimistic, state ends
      // up with BOTH — a visible duplicate that won't dedup on subsequent
      // polls if server text and client text don't match byte-for-byte.
      // Updating in place means: if streamingMsg is still here, it becomes
      // the final bubble (still optimistic-prefixed → next merge dedups
      // against asst_real). If it's already gone, the asst_real is already
      // in state — no duplicate to add.
      setMessages(prev => {
        const hasStreaming = prev.some(m => m.id === streamingMsgId);
        const withUserUnpending = prev.map(m =>
          m.id === userMsgId ? { ...m, pending: false } : m,
        );
        if (!hasStreaming) return withUserUnpending;
        return withUserUnpending.map(m =>
          m.id === streamingMsgId
            ? { ...m, pending: false, content: responseContent }
            : m,
        );
      });

      const replyText = responseText.trim();
      emitTelemetry('chat_message_sent', {
        sessionId,
        props: {
          role: 'assistant',
          text: replyText.slice(0, 1000),
          length: replyText.length,
          interaction_id: state.finalPayload?.interaction_id ?? undefined,
          streamed: true,
        },
      });

      setSending(false);
    },
    [sessionId, sending, locale],
  );

  const submit = useCallback(() => {
    submitText(draft);
  }, [draft, submitText]);

  // Queued submit from the parent's collapsed-launcher suggestion pills.
  // The bootstrap posts `wg:queued_submit` either at click time (if the
  // SPA was already booted) or replays it at wg:ready (cold open). We
  // wait until sessionId is bound before firing — otherwise submitText
  // bails. Each queued submit is consumed exactly once.
  const consumedQueuedSubmits = useRef(new Set<string>());
  useEffect(() => {
    if (!sessionId) return;
    const onMsg = (ev: MessageEvent) => {
      const data = ev?.data as
        | { type?: string; payload?: { text?: string } }
        | undefined;
      if (!data || data.type !== 'wg:queued_submit') return;
      const text = (data.payload?.text || '').trim();
      if (!text) return;
      // Dedup using text+timestamp-ish key — same text within 2s is one
      // tap; outside that window is a deliberate re-submit.
      const dedupKey = `${text}|${Math.floor(Date.now() / 2000)}`;
      if (consumedQueuedSubmits.current.has(dedupKey)) return;
      consumedQueuedSubmits.current.add(dedupKey);
      submitText(text);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [sessionId, submitText]);

  // Auto-grow the composer textarea so it expands vertically (Apple
  // Messages style) up to its CSS max-height, then hands off to internal
  // scrolling. When the draft is emptied (post-submit or chip-tap), clear
  // the inline height entirely so CSS min-height (1 line) wins — without
  // this the textarea kept the last-typed multi-line height even after
  // setDraft('') because scrollHeight on an empty textarea reports the
  // post-content layout for one frame.
  const draftRef = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    const el = draftRef.current;
    if (!el) return;
    if (!draft) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [draft]);

  // Scroll model: anchor on the user's latest request message, NOT the
  // bottom of the thread. Pinning to bottom means a multi-card assistant
  // reply scrolls the user's question off the top and the visitor loses
  // the context of what they asked. Anchoring the user msg at the top
  // keeps the question visible and lets the reply unfold (with the
  // typing-dots bubble) right underneath.
  //
  // submitText() pushes BOTH the userMsg and the streamingMsg in one
  // setState, so the array tail is the streaming bubble, not the
  // question. Looking at messages[length-1] alone misses the user msg
  // and the effect bails — that's the long-thread bug where a tap of
  // Send did nothing visible (typing dots and question both stuck off-
  // screen below the fold). Fix: walk the freshly-added range backward
  // to locate the most recent user message and anchor that.
  //
  // Three modes:
  //   • initial mount  → pin to bottom (show latest history)
  //   • new user msg   → scroll that msg to the top of the viewport so
  //                       both the question AND the typing bubble below
  //                       are visible immediately on send
  //   • streaming reply (deltas / content items) → don't re-scroll,
  //     let the assistant reply grow below the user msg
  //
  // History grows monotonically; we only act on count growth.
  //
  // (Why not column-reverse? It auto-pins to bottom and is great for
  // pure "tail-of-conversation" UIs, but it doesn't help us anchor the
  // user's question at the TOP. The two patterns solve different
  // problems; sticking with explicit JS scroll keeps anchor flexibility.)
  const didInitialScrollRef = useRef(false);
  const lastSeenLengthRef = useRef(0);
  const lastUserMsgIdRef = useRef<string | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || messages.length === 0) return;

    // Initial mount: pin to bottom once so the visitor lands on the
    // latest history. Re-pin after fonts settle (metric shifts).
    if (!didInitialScrollRef.current) {
      didInitialScrollRef.current = true;
      lastSeenLengthRef.current = messages.length;
      // Seed the ref to the most recent user msg already in history so
      // we don't re-anchor it on first render.
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].sender === 'user') {
          lastUserMsgIdRef.current = messages[i].id;
          break;
        }
      }
      const pin = () => {
        el.scrollTop = el.scrollHeight;
      };
      requestAnimationFrame(pin);
      void document.fonts?.ready?.then(() => requestAnimationFrame(pin));
      return;
    }

    // No new bubbles since last run — bail. Streaming-content effects
    // (deltas, mid-stream cards) don't trigger this.
    if (messages.length <= lastSeenLengthRef.current) return;
    const prevLength = lastSeenLengthRef.current;
    lastSeenLengthRef.current = messages.length;

    // Walk the newly-added slice from the end, looking for a user msg
    // we haven't already anchored. Submit pushes [userMsg, streamingMsg]
    // together so we typically find the user msg at length-2; an
    // assistant-only landing (e.g. polled history merge) yields no user
    // msg in the slice and we leave scroll alone.
    let newUser: (typeof messages)[number] | null = null;
    for (let i = messages.length - 1; i >= prevLength; i--) {
      if (
        messages[i].sender === 'user' &&
        messages[i].id !== lastUserMsgIdRef.current
      ) {
        newUser = messages[i];
        break;
      }
    }
    if (!newUser) return;
    lastUserMsgIdRef.current = newUser.id;

    // Defer until BOTH the new user bubble AND the streaming bubble
    // beneath it have rendered, so offsetTop reflects post-layout
    // reality. Two rAFs covers React commit + browser paint + the
    // Markdown lazy-load chunk that mounts the assistant text.
    const scrollUserMsgToTop = () => {
      const target = el.querySelector<HTMLElement>(
        `[data-msg-id="${CSS.escape(newUser!.id)}"]`,
      );
      if (!target) {
        // Fallback — pin to bottom if we can't locate the msg DOM.
        el.scrollTop = el.scrollHeight;
        return;
      }
      // Look for an immediately-preceding stamp so the timestamp stays
      // in view above the question. previousElementSibling traversal
      // is fine because stamps are simple sibling divs.
      const stamp = target.previousElementSibling?.classList.contains(
        'wg-chat__stamp',
      )
        ? (target.previousElementSibling as HTMLElement)
        : null;
      const anchor = stamp ?? target;
      // offsetTop is relative to the offsetParent (the scroller), so
      // setting scrollTop to it lands the anchor at the top edge.
      // Smooth scroll feels right here — the visitor JUST tapped Send,
      // they want to see their question move into place, not snap.
      el.scrollTo({ top: anchor.offsetTop, behavior: 'smooth' });
    };
    requestAnimationFrame(() => requestAnimationFrame(scrollUserMsgToTop));
  }, [messages]);

  const grouped = useMemo(
    () =>
      messages.map((m, i) => {
        const prev = messages[i - 1];
        return {
          msg: m,
          showStamp: !prev || formatStamp(prev.at) !== formatStamp(m.at),
        };
      }),
    [messages],
  );

  // Build a map of prompt_id → user's structured answer. Renderers consume
  // this via `ctx.answeredWith` to display chosen options inert. We only
  // know about answers the user made in this session (server doesn't yet
  // round-trip metadata through /history); good enough for the live thread.
  const answersByPromptId = useMemo(() => {
    const map: Record<string, unknown> = {};
    for (const m of messages) {
      if (m.sender !== 'user') continue;
      const meta = m.meta;
      if (!meta || typeof meta.in_response_to !== 'string') continue;
      map[meta.in_response_to] = meta.structured_value;
    }
    return map;
  }, [messages]);

  // Build the partner-themed render context once per render. Most renderers
  // read theme via CSS custom properties (set via `themeToCssVars` on the
  // root), so prop-drilling theme is rare — but interactive renderers also
  // read `onSendMessage` and `isHistory` from this object.
  const partnerTheme = useMemo(() => {
    const themeOverride =
      (config?.theme as Partial<typeof DEFAULT_THEME> | undefined) || {};
    return { ...DEFAULT_THEME, ...themeOverride };
  }, [config?.theme]);

  const onSendMessageCb = useCallback(
    (text: string, meta?: ResponseMeta) => {
      // Optimistically render the user's chip-tap reply, fire the chat
      // request with metadata so the agent receives the structured value.
      if (!text) return;
      submitText(text, meta);
    },
    [submitText],
  );

  // Action dispatcher dependency bag. Built here once per render so
  // CardRenderer / AppActionRenderer / PageActionRenderer can fire side
  // effects (navigate, prefill_checkout, highlight, …) without per-renderer
  // wiring. See chat/actions.ts for the verb routing.
  const actionDeps: ActionDeps = useMemo(
    () => ({
      setView,
      setCart,
      setPrefillContact,
      addItineraryItem,
      removeItineraryItem,
      onSendMessage: onSendMessageCb,
      parentBridge: {
        send: parentBridge.send,
        requestClose: parentBridge.requestClose,
        dispatchAction: parentBridge.dispatchAction,
        requestLayout: parentBridge.requestLayout,
        layoutMax: parentBridge.layoutMax,
      },
      currentView,
      // Used by prefill_checkout fallback when an action carries only
      // {provider_id, product_id} (admin seed cards) instead of a fully-
      // built line_item (brain emissions). Session-scoped: requires a
      // bound chatSessionId so the chat-router can replay principal
      // headers server-side. No-op until sessionId resolves — clicks fire
      // through the warning path instead of erroring.
      buildCheckoutFromProduct: sessionId
        ? body => {
            // Compose the provider slug the /experiences/v1/{id}/book route
            // accepts. Server resolves slug → catalog UUID; brain emits the
            // same shape via its own admin-auth path.
            const itemId = `${body.provider_id}_${body.product_id}`;
            return initiateBookingForSession(
              sessionId,
              itemId,
              body,
              httpClient,
            ).then(r => ({
              booking_type: r.booking_type,
              url: r.url,
              detection_patterns: r.detection_patterns,
            }));
          }
        : undefined,
    }),
    [
      setView,
      setCart,
      setPrefillContact,
      addItineraryItem,
      removeItineraryItem,
      onSendMessageCb,
      parentBridge.send,
      parentBridge.requestClose,
      parentBridge.dispatchAction,
      parentBridge.requestLayout,
      parentBridge.layoutMax,
      currentView,
      sessionId,
    ],
  );

  // Docked chips: collect every `quick_replies` item from the most recent
  // assistant message — but only when no user reply has landed after it
  // (i.e., the chips are still "live"). Once the user types or taps, the
  // dock disappears until the next agent turn emits new chips.
  //
  // Multiple `quick_replies` items in a single turn flatten into one strip;
  // each option keeps its source `prompt_id` so the agent receives the
  // correct `in_response_to` on tap.
  const dockedQuickReplies = useMemo<
    Array<{ promptId: string; option: QuickReplyOption }>
  >(() => {
    // Empty-thread bootstrap: surface the partner's suggested questions
    // through the same chip strip the agent uses for live quick_replies.
    // Visitors get a "tap to start" affordance docked above the input
    // instead of a separate block of pills inside the welcome screen.
    // Synthetic prompt_id ("__seed__") keeps the tap routing clean — taps
    // call onSendMessage(label) which fires a normal user message rather
    // than a structured_value reply.
    // Seed suggestions are a "tap to start" affordance scoped to the
    // collapsed/native panel — when the embed has promoted to
    // expanded/maximized (desktop wide layout), the visitor has a much
    // larger empty surface and the suggestion chips read as redundant
    // chrome rather than helpful prompts. Hide them there; visitor uses
    // the input directly or whatever the agent next emits.
    const isExpanded =
      parentBridge.layoutMode === 'expanded' ||
      parentBridge.layoutMode === 'maximized';
    if (messages.length === 0 && suggestions.length > 0 && !isExpanded) {
      return suggestions.slice(0, 6).map(text => ({
        promptId: '__seed__',
        option: { label: text, value: text } as QuickReplyOption,
      }));
    }
    const out: Array<{ promptId: string; option: QuickReplyOption }> = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender === 'user') return [];
      if (m.sender !== 'assistant') continue;
      // Skip transient streaming/loading bubbles — wait for the final turn.
      if (m.pending) return [];
      const items = m.content.filter(c => c.type === 'quick_replies');
      if (items.length === 0) continue;
      for (const raw of items) {
        const it = raw as unknown as QuickRepliesContentItem;
        const promptId = it.prompt_id;
        if (!promptId) continue;
        for (const opt of it.options || []) {
          out.push({ promptId, option: opt });
        }
      }
      return out;
    }
    return [];
  }, [messages, suggestions, parentBridge.layoutMode]);

  const dockedPromptIds = useMemo(
    () => new Set(dockedQuickReplies.map(c => c.promptId)),
    [dockedQuickReplies],
  );

  const ctx: RenderCtx = useMemo(
    () => ({
      sessionId: sessionId ?? '',
      onSendMessage: onSendMessageCb,
      theme: partnerTheme,
      locale,
      isHistory: false,
      actionDeps,
      dockedPromptIds,
    }),
    [
      sessionId,
      onSendMessageCb,
      partnerTheme,
      locale,
      actionDeps,
      dockedPromptIds,
    ],
  );

  return (
    <div className="wg-chat" data-voice={voiceActive ? 'active' : undefined}>
      {!sessionId && (
        <div className="wg-chat__empty wg-chat__center">
          <Spinner size="medium" />
        </div>
      )}

      {sessionId && (
        <>
          <div ref={scrollRef} className="wg-chat__thread">
            {loading && messages.length === 0 && (
              <div className="wg-chat__center">
                <Spinner size="medium" />
              </div>
            )}
            {!loading && messages.length === 0 && (
              <div className="wg-chat__welcome">
                {/* Empty-state icon only when truly empty — partners with
                    a seed card already have visual real estate filled. */}
                {seedContentItems.length === 0 && (
                  <div className="wg-chat__empty-icon" aria-hidden>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12a8 8 0 0 1-11.6 7.15L4 21l1.86-5.43A8 8 0 1 1 21 12z" />
                      <line x1="8.5" y1="11.5" x2="8.5" y2="11.5" />
                      <line x1="12" y1="11.5" x2="12" y2="11.5" />
                      <line x1="15.5" y1="11.5" x2="15.5" y2="11.5" />
                    </svg>
                  </div>
                )}
                <p className="wg-chat__welcome-copy">{welcomeCopy}</p>
                {/* Suggested questions used to live here as pill buttons.
                    They now surface in the chip dock above the input via
                    dockedQuickReplies' empty-thread bootstrap path — same
                    affordance, more discoverable place. */}
                {seedContentItems.length > 0 && (
                  <MessageBubble
                    message={{
                      id: '__seed__',
                      sender: 'assistant',
                      content: seedContentItems,
                      at: new Date().toISOString(),
                    }}
                    ctx={ctx}
                    isHistoryItem={false}
                    answersByPromptId={answersByPromptId}
                  />
                )}
              </div>
            )}
            {grouped.map(({ msg, showStamp }, idx) => {
              // Past messages render with isHistory=true so interactive items
              // (chips/forms) display inert "answered" pills rather than live.
              // Also: any interactive bubble that's been answered (regardless
              // of position) goes inert — once a chip is tapped its prompt
              // is decided, even if it happens to be the most recent bubble.
              const isHistoryItem = idx < grouped.length - 1;
              return (
                <React.Fragment key={msg.id}>
                  {showStamp && (
                    <div className="wg-chat__stamp">{formatStamp(msg.at)}</div>
                  )}
                  <MessageBubble
                    message={msg}
                    ctx={ctx}
                    isHistoryItem={isHistoryItem}
                    answersByPromptId={answersByPromptId}
                  />
                </React.Fragment>
              );
            })}
          </div>
          {dockedQuickReplies.length > 0 && !sending && !voiceActive && (
            <div
              className="wg-chat__chipdock"
              role="group"
              aria-label="Suggested replies"
            >
              <div className="wg-chat__chipdock-row">
                {dockedQuickReplies.map(({ promptId, option }, idx) => {
                  const cls = [
                    'wg-chat__chip',
                    'wg-chat__chipdock-chip',
                    option.weight ? `wg-chat__chip--${option.weight}` : null,
                  ]
                    .filter(Boolean)
                    .join(' ');
                  // __seed__ taps fire as plain user messages (no
                  // in_response_to / structured_value) — they're not
                  // answers to an interactive prompt, they're prebuilt
                  // starter questions. Live agent chips keep their
                  // structured-reply metadata so the agent can route the
                  // tap back to the right prompt_id.
                  const isSeed = promptId === '__seed__';
                  return (
                    <button
                      key={`${promptId}-${option.value}-${idx}`}
                      type="button"
                      className={cls}
                      onClick={() => {
                        if (isSeed) {
                          emitTelemetry('suggestion_tapped', {
                            sessionId,
                            props: {
                              text: option.label,
                              index: idx,
                              source: usingDefaults ? 'default' : 'partner',
                            },
                          });
                          onSendMessageCb(option.label);
                        } else {
                          onSendMessageCb(option.label, {
                            in_response_to: promptId,
                            structured_value: option.value,
                          });
                        }
                      }}
                    >
                      {option.icon && (
                        <span className="wg-chat__chip-icon">
                          {option.icon}
                        </span>
                      )}
                      <span className="wg-chat__chip-label">
                        {option.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <form
            className="wg-chat__input"
            onSubmit={e => {
              e.preventDefault();
              if (!voiceActive) submit();
            }}
          >
            {voiceEnabled && (
              <div className="wg-chat__orb-slot" aria-hidden={false}>
                <MicOrb />
              </div>
            )}
            {voiceActive ? (
              <span className="wg-chat__voice-spacer" aria-hidden />
            ) : (
              <>
                <textarea
                  ref={draftRef}
                  rows={1}
                  placeholder={t('chat.type_a_message')}
                  value={draft}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    // Enter sends; Shift+Enter (and IME composition) keep the
                    // newline behavior so multi-line drafts stay possible.
                    if (
                      e.key === 'Enter' &&
                      !e.shiftKey &&
                      !e.nativeEvent.isComposing
                    ) {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  disabled={sending}
                />
                <button
                  type="submit"
                  className="wg-chat__send"
                  aria-label={t('common.send')}
                  disabled={!draft.trim() || sending}
                >
                  <SendIcon />
                </button>
              </>
            )}
          </form>
          {error && <div className="wg-chat__error">{error}</div>}
        </>
      )}
    </div>
  );
};

export default ChatView;

const MessageBubble: React.FC<{
  message: ThreadMessage;
  ctx: import('../chat/registry').RenderCtx;
  isHistoryItem: boolean;
  answersByPromptId: Record<string, unknown>;
}> = ({ message, ctx, isHistoryItem, answersByPromptId }) => {
  const isBot = message.sender === 'assistant';
  return (
    <div
      className={`wg-chat__msg wg-chat__msg--${isBot ? 'bot' : 'user'}${
        message.failed ? ' is-failed' : ''
      }`}
      data-msg-id={message.id}
    >
      <span className="wg-chat__msg-bubble">
        {message.content.map((c, i) => {
          // Assistant text bubbles render through Markdown so * lists,
          // **bold**, [links] and code blocks come through naturally.
          // User-typed messages stay plain (we don't markdown-render
          // user input — they didn't opt into formatting).
          const itemForRender =
            isBot && c.type === 'text'
              ? ({ type: 'markdown', markdown: String(c.text ?? '') } as const)
              : c;
          // Per-item ctx — interactive items go inert once a matching
          // user-reply has been recorded, OR if this bubble is in the past.
          const promptId = (c as { prompt_id?: unknown }).prompt_id;
          const matched =
            typeof promptId === 'string'
              ? answersByPromptId[promptId]
              : undefined;
          const inert = isHistoryItem || matched !== undefined;
          const itemCtx: import('../chat/registry').RenderCtx = {
            ...ctx,
            isHistory: inert,
            answeredWith: matched,
          };
          return renderItem(itemForRender as never, itemCtx, i);
        })}
      </span>
    </div>
  );
};

// Server-side timestamps from log_interaction_sync arrive without a UTC suffix
// ("2026-05-03T05:57:43" instead of "...Z"). JS interprets such strings as the
// *local* zone — which silently shifts every server message by the user's UTC
// offset. For non-UTC users that breaks two things: chat stamps look stuck in
// UTC, AND the merge-dedup time window is blown out by a TZ-offset-sized diff
// against the optimistic ISO-with-Z timestamps, so request messages survive
// dedup as visible duplicates. Both call sites use this helper to coerce
// missing-zone strings to UTC.
/**
 * Pull (catalog_product_id, title) pairs out of an assistant message's
 * content_items. Mirror of `_extract_surfaced_ids` in tests/e2e/conftest.py.
 *
 * Cards from `search_activities` / `present_itinerary` / `discover_for_visitor`
 * embed the catalog_product_id in either an action's `data.activity_id` (the
 * canonical place — that's what `add_to_trip` consumes) or on the card's own
 * `data.activity_id` / `data.catalog_product_id` / `data.id`. We walk both
 * spots and skip cards without a resolvable id. Cap at 8 to keep the history
 * payload bounded.
 */
function extractSurfacedIds(items: ChatMessageItem[]): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const it of items ?? []) {
    if (!it || it.type !== 'card') continue;
    let cid: string | null = null;
    for (const action of (
      it as { actions?: Array<{ data?: Record<string, unknown> }> }
    ).actions ?? []) {
      const data = action?.data;
      if (data && typeof data === 'object') {
        const candidate =
          data['activity_id'] ?? data['catalog_product_id'] ?? data['id'];
        if (typeof candidate === 'string' && candidate.trim()) {
          cid = candidate;
          break;
        }
      }
    }
    if (!cid) {
      const data = (it as { data?: Record<string, unknown> }).data;
      if (data && typeof data === 'object') {
        const candidate =
          data['activity_id'] ?? data['catalog_product_id'] ?? data['id'];
        if (typeof candidate === 'string' && candidate.trim()) {
          cid = candidate;
        }
      }
    }
    if (!cid) continue;
    const title = String((it as { title?: string }).title ?? '').slice(0, 80);
    out.push([cid, title]);
    if (out.length >= 8) break;
  }
  return out;
}

function parseUtcTime(iso: string): number {
  if (!iso) return NaN;
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
  const normalized = iso.includes(' ') ? iso.replace(' ', 'T') : iso;
  return new Date(hasZone ? normalized : `${normalized}Z`).getTime();
}

function formatStamp(iso: string): string {
  try {
    const ms = parseUtcTime(iso);
    if (Number.isNaN(ms)) return iso;
    return new Date(ms).toLocaleString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      weekday: 'short',
    });
  } catch {
    return iso;
  }
}

function SendIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
